/**
 * Restriction taxonomique d'un goal d'enrichissement.
 *
 * Un goal (collection Firestore `goals`, configuré dans TagManagement) peut
 * restreindre les espèces auxquelles il s'applique selon deux modes :
 *   - 'exclude' (legacy / défaut) : s'applique à TOUT SAUF les ordres/familles/espèces listés
 *   - 'include' : s'applique UNIQUEMENT aux ordres/familles/espèces listés
 *
 * Champs Firestore :
 *   restrictionMode: 'exclude' | 'include'           (absent ⇒ 'exclude')
 *   orderBlackList / famillyBlackList / specieBlacklist   (mode exclude)
 *   orderWhiteList / famillyWhiteList / specieWhitelist   (mode include)
 *   chaque entrée = { label, value } où value = orderId / familyId / specieId
 *
 * Un "taxon" = { specieId, familyId, orderId } dérivé d'une espèce
 * (doc `species`, familyId/orderId au niveau racine, nullables pour espèces IUCN).
 *
 * NB : logique identique côté mobile (WellfareMonitor2 app/utils/enrich/goalRestriction.js).
 */

/** Transforme une liste [{label, value}] (ou [value]) en Set de valeurs non vides. */
const toValueSet = (list) => {
  const set = new Set();
  if (Array.isArray(list)) {
    list.forEach((entry) => {
      const value = entry && typeof entry === "object" ? entry.value : entry;
      if (value !== undefined && value !== null && value !== "") set.add(value);
    });
  }
  return set;
};

/**
 * Normalise la restriction d'un goal en
 * { mode, orderIds:Set, familyIds:Set, specieIds:Set }.
 * Rétro-compatibilité : sans restrictionMode, on lit les blackLists (exclude).
 */
export const getGoalRestriction = (goal) => {
  const empty = {
    mode: "exclude",
    orderIds: new Set(),
    familyIds: new Set(),
    specieIds: new Set(),
  };
  if (!goal) return empty;

  const mode = goal.restrictionMode === "include" ? "include" : "exclude";
  if (mode === "include") {
    return {
      mode,
      orderIds: toValueSet(goal.orderWhiteList),
      familyIds: toValueSet(goal.famillyWhiteList),
      specieIds: toValueSet(goal.specieWhitelist),
    };
  }
  return {
    mode,
    orderIds: toValueSet(goal.orderBlackList),
    familyIds: toValueSet(goal.famillyBlackList),
    specieIds: toValueSet(goal.specieBlacklist),
  };
};

/** Aucune liste renseignée ⇒ pas de restriction effective. */
const restrictionIsEmpty = (r) =>
  r.orderIds.size === 0 && r.familyIds.size === 0 && r.specieIds.size === 0;

/** Le taxon est-il présent dans les listes (espèce OU famille OU ordre) ? */
const taxonMatchesLists = (restriction, taxon) => {
  if (!taxon) return false;
  return (
    (taxon.specieId != null && restriction.specieIds.has(taxon.specieId)) ||
    (taxon.familyId != null && restriction.familyIds.has(taxon.familyId)) ||
    (taxon.orderId != null && restriction.orderIds.has(taxon.orderId))
  );
};

/**
 * Le goal s'applique-t-il à UN taxon donné ?
 *  - exclude : s'applique sauf si le taxon est listé (listes vides ⇒ s'applique à tout)
 *  - include : s'applique uniquement si le taxon est listé (listes vides ⇒ pas de
 *    restriction effective ⇒ s'applique à tout, pour ne pas tout masquer sur une
 *    configuration incomplète)
 */
export const goalAppliesToTaxon = (goal, taxon) => {
  const r = getGoalRestriction(goal);
  if (restrictionIsEmpty(r)) return true;
  const matches = taxonMatchesLists(r, taxon);
  return r.mode === "include" ? matches : !matches;
};

/**
 * Le goal s'applique-t-il à AU MOINS UN taxon de la liste (enclos multi-espèces) ?
 * Liste vide / absente ⇒ true (fail-open : on ne masque rien si la taxonomie de
 * l'enclos est inconnue).
 */
export const goalAppliesToAnyTaxon = (goal, taxa) => {
  if (!Array.isArray(taxa) || taxa.length === 0) return true;
  const r = getGoalRestriction(goal);
  if (restrictionIsEmpty(r)) return true;
  if (r.mode === "include") {
    return taxa.some((taxon) => taxonMatchesLists(r, taxon));
  }
  // exclude : applicable s'il reste au moins une espèce non exclue dans l'enclos
  return taxa.some((taxon) => !taxonMatchesLists(r, taxon));
};

/** Le goal porte-t-il une restriction effective ? (debug / UI) */
export const goalHasRestriction = (goal) =>
  !restrictionIsEmpty(getGoalRestriction(goal));
